import React, { useState, useEffect } from 'react';
import { adminApi, payPeriodsApi, formatDate, formatDateRange } from '../../lib/api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Download,
  DollarSign,
  TrendingUp,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const AdminCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCommissions, setSelectedCommissions] = useState([]);

  useEffect(() => {
    fetchPayPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchCommissions();
    }
  }, [selectedPeriod, statusFilter]);

  const fetchPayPeriods = async () => {
    try {
      const response = await payPeriodsApi.getAll();
      setPayPeriods(response.data);
      const current = response.data.find(p => p.is_current);
      if (current) {
        setSelectedPeriod(current.start_date);
      }
    } catch (error) {
      toast.error('Failed to load pay periods');
    }
  };

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getCommissions({
        payPeriodStart: selectedPeriod,
        status: statusFilter
      });
      setCommissions(response.data);
      setSelectedCommissions([]);
    } catch (error) {
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (commissionId, action) => {
    try {
      await adminApi.actionOnCommission(commissionId, action);
      toast.success(`Commission ${action}d`);
      fetchCommissions();
    } catch (error) {
      toast.error(`Failed to ${action} commission`);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCommissions.length === 0) {
      toast.error('No commissions selected');
      return;
    }
    
    try {
      await adminApi.bulkCommissionAction(selectedCommissions, action);
      toast.success(`${selectedCommissions.length} commission(s) ${action}d`);
      fetchCommissions();
    } catch (error) {
      toast.error(`Failed to ${action} commissions`);
    }
  };

  const handleExport = async () => {
    if (!selectedPeriod) {
      toast.error('Please select a reporting period');
      return;
    }
    
    try {
      const response = await adminApi.exportCommissions(selectedPeriod);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commissions_${selectedPeriod}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded');
    } catch (error) {
      const message = error.response?.status === 404 
        ? 'No commissions found for this period'
        : 'Failed to export CSV';
      toast.error(message);
    }
  };

  const toggleSelection = (id) => {
    setSelectedCommissions(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pendingCommissions = commissions.filter(c => c.status === 'pending');
    if (selectedCommissions.length === pendingCommissions.length) {
      setSelectedCommissions([]);
    } else {
      setSelectedCommissions(pendingCommissions.map(c => c.id));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
            <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
            <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
            Pending
          </Badge>
        );
    }
  };

  const getProductLabel = (type) => {
    return type === 'brickai_membership' ? 'BrickAI Membership' : 'Website';
  };

  const pendingCommissions = commissions.filter(c => c.status === 'pending');
  const totalCommission = commissions.reduce((sum, c) => sum + c.commission_amount, 0);
  const totalSales = commissions.reduce((sum, c) => sum + c.sale_amount, 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-commissions">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Commissions
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Review and approve sales commissions
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleExport}
            className="border-brick-cyan text-brick-cyan hover:bg-brick-cyan hover:text-white"
            data-testid="export-commissions-btn"
          >
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">
                  Reporting Period
                </label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger data-testid="period-select" className="border-slate-300">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {payPeriods.map((period) => (
                      <SelectItem key={period.start_date} value={period.start_date}>
                        {formatDateRange(period.start_date, period.end_date)}
                        {period.is_current && ' (Current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[150px]">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">
                  Status
                </label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter" className="border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Sales</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-sales">
                    ${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-brick-cyan/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-brick-cyan" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Commission</p>
                  <p className="text-2xl font-bold text-green-600 mt-1" data-testid="total-commission">
                    ${totalCommission.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="pending-count">{pendingCommissions.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Entries</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-entries">{commissions.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-slate-600" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Actions */}
        {pendingCommissions.length > 0 && (
          <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <Checkbox 
              checked={selectedCommissions.length === pendingCommissions.length && pendingCommissions.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all-checkbox"
            />
            <span className="text-sm text-slate-600">
              {selectedCommissions.length} selected
            </span>
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('approve')}
                disabled={selectedCommissions.length === 0}
                className="text-green-600 border-green-500 hover:bg-green-50"
                data-testid="bulk-approve-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" aria-hidden="true" />
                Approve Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('reject')}
                disabled={selectedCommissions.length === 0}
                className="text-red-600 border-red-500 hover:bg-red-50"
                data-testid="bulk-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1" aria-hidden="true" />
                Reject Selected
              </Button>
            </div>
          </div>
        )}

        {/* Commission List */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg">Commissions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-pulse text-slate-500">Loading...</div>
              </div>
            ) : commissions.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" aria-hidden="true" />
                <p className="text-slate-500">No commissions found for this period</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {commissions.map((commission) => (
                  <div
                    key={commission.id}
                    className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`commission-row-${commission.id}`}
                  >
                    {commission.status === 'pending' && (
                      <Checkbox
                        checked={selectedCommissions.includes(commission.id)}
                        onCheckedChange={() => toggleSelection(commission.id)}
                        data-testid={`commission-checkbox-${commission.id}`}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-slate-900">{commission.user_name}</span>
                        <span className="text-slate-300">•</span>
                        <span className="font-mono text-sm text-brick-cyan">{formatDate(commission.date)}</span>
                        {getStatusBadge(commission.status)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="font-medium">{commission.client_name}</span>
                        <Badge variant="outline" className="text-xs border-slate-300">{getProductLabel(commission.product_type)}</Badge>
                        {commission.description && (
                          <span className="text-slate-400 truncate max-w-xs">— {commission.description}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right mr-4">
                      <p className="text-sm text-slate-500">Sale: ${commission.sale_amount.toLocaleString()}</p>
                      <p className="font-mono text-lg font-bold text-green-600">
                        +${commission.commission_amount.toFixed(2)}
                      </p>
                    </div>
                    {commission.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAction(commission.id, 'approve')}
                          className="hover:bg-green-50"
                          data-testid={`approve-${commission.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 text-green-600" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAction(commission.id, 'reject')}
                          className="hover:bg-red-50"
                          data-testid={`reject-${commission.id}`}
                        >
                          <XCircle className="w-4 h-4 text-red-600" aria-hidden="true" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminCommissions;
